/**
 * Dashboard config types — shared between the (current) static renderer
 * and the (B0.9 editor) drag-and-drop authoring surface.
 *
 * Per docs/06_iot_twin_alignment.md § 5 — Entity Alias + Tag Keys is the
 * data-source mental model. Widget instances are datasource-type-agnostic.
 *
 * B0.32 introduces **slot-based** data binding. Widgets declare named
 * slots (X axis, Y axis, Series, Categories, …) and the picker fills
 * each slot from the alias's available keys, filtering by the
 * DataKind compatibility declared on the slot.
 */

import type { DataKind } from "@foundry/foundry-plant";

export type WidgetType =
  | "kpi"
  | "gauge"
  | "timeseries"
  | "alarm-list"
  | "event-feed"
  | "status-grid"
  | "defect-pareto"
  | "pareto"
  | "spc"
  | "histogram"
  | "table"
  | "correlation-heatmap"
  | "scatter"
  | "oee"
  | "equipment-overview"
  | "markdown"
  | "image";

/**
 * One named binding slot on a widget. Each widget declares zero or more
 * slots in `WidgetRegistryEntry.accepts`. The picker enforces:
 *   - `required` slots must have at least one key
 *   - `multi: false` accepts exactly one key (replace-on-pick)
 *   - keys whose classified DataKind isn't in `dataKinds` are shown
 *     disabled with an explanatory tooltip
 */
export interface WidgetSlot {
  id: string;
  label: string;
  role: "x" | "y" | "value" | "trend" | "series" | "categories" | "matrix" | "events" | "state";
  required: boolean;
  multi: boolean;
  dataKinds: DataKind[];
  /** Optional one-line help shown under the slot label in the picker. */
  hint?: string;
}

/**
 * Two-layered data source — first an Entity/Tag alias, then per-slot keys.
 *
 * Storage: `slots: Record<slotId, keys[]>` is canonical from B0.32 onward.
 * The legacy `keys: string[]` field is kept on the type for back-compat
 * with saved dashboards and pack-shipped JSON; the renderer reads via
 * `readSlot()` below which merges both shapes.
 */
export interface DataSource {
  kind: "tag" | "port";
  /** Alias label (e.g. "Furnace 1" or "All induction furnaces"). */
  alias: string;
  /**
   * B6 (2026-10-19): a port binding by machine. The alias is then the machine's entity id and the keys its ports, each
   * read through the machine's roles (GET /v1/twin/equipment/{id}/roles: the port's one primary stream tag), never a
   * tag guessed by its name; a port fed by two tags says so and draws nothing (port-binding.ts).
   */
  byEntity?: boolean;
  /** Per-slot key bindings. Slot ids match the widget's `accepts[].id`. */
  slots?: Record<string, string[]>;
  /**
   * @deprecated B0.32 — pre-slot flat key list. Treated as `slots.value`
   * when present. New code writes `slots` only.
   */
  keys?: string[];
  /**
   * B2.3a — if set, the widget reads from `lib/imports/store.tsx` (an
   * ImportedDataset's rows) instead of the live tag stream. Used by
   * auto-generated reports spawned from a customer Excel import. The
   * underlying dataset is owned by the same tenant and gitignored
   * locally (see `data/`).
   */
  importedDatasetId?: string;
}

/**
 * Read the keys bound to a given slot. Handles both the new `slots`
 * shape and the legacy `keys` shape (treats it as the default slot).
 */
export function readSlot(
  ds: DataSource | undefined,
  slotId: string,
): string[] {
  if (!ds) return [];
  if (ds.slots && ds.slots[slotId]) return ds.slots[slotId];
  // Legacy compat: a flat `keys` list maps to whichever slot the widget
  // treats as its default ("value" or "series", typically).
  if (ds.keys && (slotId === "value" || slotId === "series")) {
    return ds.keys;
  }
  return [];
}

/**
 * Write a slot binding immutably, preserving other slots.
 */
export function writeSlot(
  ds: DataSource,
  slotId: string,
  keys: string[],
): DataSource {
  const nextSlots = { ...(ds.slots ?? {}), [slotId]: keys };
  // Drop legacy `keys` field on first write so we converge on slot storage.
  const { keys: _legacy, ...rest } = ds;
  return { ...rest, slots: nextSlots };
}

/** Grid placement — react-grid-layout compatible. */
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One widget instance on a dashboard. */
export interface WidgetInstance {
  id: string;
  type: WidgetType;
  title?: string;
  subtitle?: string;
  layout: WidgetLayout;
  /** Optional data source — some widgets (markdown) don't need one. */
  dataSource?: DataSource;
  /**
   * Per-widget config — type-specific.
   *
   * Common keys read by the chart widgets (timeseries/gauge/histogram/spc/
   * scatter): `yMin?: number` / `yMax?: number` fix the value (Y) axis
   * range. Absent → autorange (the default). See `widget-registry.tsx`.
   */
  config?: Record<string, unknown>;
}

/** Complete dashboard config. */
export interface DashboardConfig {
  id: string;
  name: string;
  description?: string;
  /** Number of columns in the responsive grid. Default 12. */
  cols?: number;
  /** Default row height in px. Default 80. */
  rowHeight?: number;
  widgets: WidgetInstance[];
  /** Audience source (display badge). */
  source: "my" | "pack" | "shared";
  /** Pack id when source === "pack". */
  pack?: string;
  /** Owner display name. */
  owner: string;
  /** Last modified ISO timestamp. */
  updatedAt: string;
}
